import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sidebar as BarteSidebar, MenuLeaf, Submenu, Icon } from 'barte-design-system';

import { ROUTES_LEGACY_WEBAPP } from '@payfac/constants';
import { useFeatureToggle } from '@payfac/hooks';

import { getRoutesApp } from './sidebar.constants';

import styles from './sidebar.module.css';
import type { SidebarRouteItemProps, SidebarSubRouteItemProps } from './sidebar.type';

export const Sidebar = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { isEnabled: isEnableNewRatePlanPage } = useFeatureToggle("newRatePlanPage");
    const { isEnabled: isEnablePendenciesPage } = useFeatureToggle("pendenciesPage");
    const { isEnabled: isEnableTransactionMetabaseScreen } = useFeatureToggle("transactionMetabaseScreen");
    const { isEnabled: isEnableIndicatorsMetabaseScreen } = useFeatureToggle("indicatorsMetabaseScreen");
    const { isEnabled: isEnableTerminalsMetabaseScreen } = useFeatureToggle("terminalsMetabaseScreen");
    const { isEnabled: isEnableRatePartnersScreen } = useFeatureToggle("ratePartnersScreen");
    const { isEnabled: isEnableSellerListScreen } = useFeatureToggle("sellerListScreen");

    const [isOpen, setIsOpen] = useState(false);
    const [openPaths, setOpenPaths] = useState<string[]>([]);

    const routesApp = useMemo(
        () => getRoutesApp(t, {
            isEnableNewRatePlanPage,
            isEnablePendenciesPage,
            isEnableTransactionMetabaseScreen,
            isEnableIndicatorsMetabaseScreen,
            isEnableTerminalsMetabaseScreen,
            isEnableRatePartnersScreen,
            isEnableSellerListScreen,
        }),
        [t, isEnableNewRatePlanPage, isEnablePendenciesPage, isEnableTransactionMetabaseScreen, isEnableIndicatorsMetabaseScreen, isEnableTerminalsMetabaseScreen, isEnableRatePartnersScreen, isEnableSellerListScreen],
    );

    useEffect(() => {
        const activeParentPaths = routesApp
            .filter(route =>
                route.items?.some(item =>
                    pathname.includes(item.path.replace(ROUTES_LEGACY_WEBAPP.path, '')) || window.location.href.includes(item.path)
                )
            )
            .map(route => route.path);

        if (activeParentPaths.length > 0) {
            setOpenPaths(prev => Array.from(new Set([...prev, ...activeParentPaths])));
        }
    }, [pathname, routesApp]);

    const handleMenuClick = (route: SidebarRouteItemProps) => {
        if (route.isExternal) {
            window.location.href = route.path;
        } else {
            navigate(route.path);
        }
    };

    const handleSubmenuClick = (subPath: string, parentRoute: SidebarRouteItemProps, isExternalSub?: boolean) => {
        const isExternal = isExternalSub ?? parentRoute.isExternal;
        if (isExternal) {
            window.location.href = subPath;
        } else {
            navigate(subPath);
        }
    };

    const toggleMenuOpen = (path: string) => {
        setOpenPaths(prev =>
            prev.includes(path)
                ? prev.filter(p => p !== path)
                : [...prev, path]
        );
    };

    const renderMenuItem = (route: SidebarRouteItemProps) => {
        const { icon: PhosphorIcon, path, title, items } = route;
        const hasSubmenu = items && items.length > 0;

        const isActive = hasSubmenu
            ? items.some((item: SidebarSubRouteItemProps) => pathname.includes(item.path.replace(ROUTES_LEGACY_WEBAPP.path, '')) || window.location.href.includes(item.path))
            : pathname === path || window.location.href.includes(path);

        const isMenuOpen = openPaths.includes(path);

        if (hasSubmenu) {
            return (
                <MenuLeaf
                    key={title}
                    label={title}
                    icon={<Icon icon={PhosphorIcon} />}
                    isActive={isActive}
                    isOpen={isMenuOpen}
                    onClick={() => toggleMenuOpen(path)}
                >
                    {items.map((sub: SidebarSubRouteItemProps) => {
                        const isSubActive = pathname.includes(sub.path.replace(ROUTES_LEGACY_WEBAPP.path, '')) || window.location.href.includes(sub.path);
                        return (
                            <Submenu
                                key={sub.path}
                                label={sub.title}
                                isActive={isSubActive}
                                onClick={() => handleSubmenuClick(sub.path, route, sub.isExternal)}
                            />
                        )
                    })}
                </MenuLeaf>
            );
        }

        return (
            <MenuLeaf
                key={title}
                label={title}
                icon={<Icon icon={PhosphorIcon} />}
                isActive={isActive}
                onClick={() => handleMenuClick(route)}
            />
        );
    };

    return (
        <BarteSidebar className={styles.sidebar}
            isCollapsed={isOpen}
            onToggleCollapse={() => setIsOpen(!isOpen)}
        >
            {routesApp.map(renderMenuItem)}
        </BarteSidebar>
    );
};
